<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import EventsAPI from 'dashboard/api/planning/events';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  event: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

const { t } = useI18n();
const dialogRef = ref(null);
const reasons = ref([]);
const selectedReasonId = ref('');
const loadingReasons = ref(false);
const loadError = ref(null);

const selectedReason = computed(() =>
  reasons.value.find(reason => reason.id === selectedReasonId.value)
);

const canConfirm = computed(
  () => !!selectedReasonId.value && !loadingReasons.value && !props.isLoading
);

const connectionId = computed(
  () =>
    props.event?.metadata?.shiji_connection_id ||
    props.event?.shiji_booking_link?.connection_id ||
    null
);

const loadReasons = async () => {
  loadingReasons.value = true;
  loadError.value = null;
  try {
    const params = {};
    if (props.event?.id) params.event_id = props.event.id;
    if (connectionId.value) params.connection_id = connectionId.value;

    const { data } = await EventsAPI.getShijiCancellationReasons(params);
    reasons.value = data?.reasons || [];
    if (reasons.value.length === 1) {
      selectedReasonId.value = reasons.value[0].id;
    }
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      t('PLANNING.EVENTS.LOST_CANCELLATION.LOAD_ERROR');
    reasons.value = [];
  } finally {
    loadingReasons.value = false;
  }
};

watch(
  () => props.modelValue,
  async open => {
    if (open) {
      selectedReasonId.value = '';
      reasons.value = [];
      await loadReasons();
      await nextTick();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  },
  { immediate: true }
);

const handleConfirm = () => {
  if (!canConfirm.value || !selectedReason.value) return;
  emit('confirm', {
    id: selectedReason.value.id,
    code: selectedReason.value.code,
    label: selectedReason.value.label,
  });
};

const handleClose = reason => {
  emit('update:modelValue', false);
  if (reason === 'cancel' || reason === 'dismiss' || reason == null) {
    emit('cancel');
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    width="md"
    :title="t('PLANNING.EVENTS.LOST_CANCELLATION.TITLE')"
    :description="t('PLANNING.EVENTS.LOST_CANCELLATION.DESCRIPTION')"
    :confirm-button-label="t('PLANNING.EVENTS.LOST_CANCELLATION.CONFIRM')"
    :cancel-button-label="t('PLANNING.EVENTS.LOST_CANCELLATION.CANCEL')"
    :disable-confirm-button="!canConfirm"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="handleClose"
    @cancel="handleClose"
  >
    <div class="flex flex-col gap-2">
      <label
        class="block text-sm font-medium text-n-slate-11"
        for="shiji-cancellation-reason"
      >
        {{ t('PLANNING.EVENTS.LOST_CANCELLATION.REASON_LABEL') }}
        <span class="text-r-500">*</span>
      </label>
      <select
        id="shiji-cancellation-reason"
        v-model="selectedReasonId"
        class="reset-base w-full rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
        :disabled="loadingReasons || isLoading"
      >
        <option value="" disabled>
          {{
            loadingReasons
              ? t('PLANNING.EVENTS.LOST_CANCELLATION.LOADING')
              : t('PLANNING.EVENTS.LOST_CANCELLATION.REASON_PLACEHOLDER')
          }}
        </option>
        <option v-for="reason in reasons" :key="reason.id" :value="reason.id">
          {{ reason.label || reason.code || reason.id }}
        </option>
      </select>
      <p v-if="loadError" class="text-xs text-r-500 mb-0">
        {{ loadError }}
      </p>
      <p
        v-else-if="!loadingReasons && !reasons.length"
        class="text-xs text-n-slate-11 mb-0"
      >
        {{ t('PLANNING.EVENTS.LOST_CANCELLATION.EMPTY') }}
      </p>
    </div>
  </Dialog>
</template>
